import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SourceForm } from '@shoflo/polymer/components/SourceForm/SourceForm';
import { getFileSourceType } from '@shoflo/polymer/utils/sourceFileHelper';
import ModalHeader from 'components/Modal/Header';

import { MEDIA_SOURCE_FORM } from 'constants/ModalTypes';

import createContentSource from 'actions/Broadcast/sources/createContentSource';
import updateContentSource from 'actions/Broadcast/sources/updateContentSource';

import clearModal from '@shoflo/studio-redux/actions/modal/clearModal/clearModal';
import hideUploadStatus from '@shoflo/studio-redux/actions/source/hideUploadStatus';

import { Modal, ModalBody } from 'reactstrap';

import { selectLastMediaSource } from 'selectors/broadcastSourcesSelector';
import { getUnassignedMediaSources } from 'utils/broadcastSourceHelper';
import { useFileUploadAction } from '@studio/metrics/hooks/useFileUploadAction';

const DEFAULT_ORDER_NUMBER = 0;

const SourceFormModal = () => {
  const {
    isOpen,
    type,
    broadcastSources: { sources },
    initialValue
  } = useSelector(({ modal, broadcastSources }) => ({
    isOpen: modal && modal.type === MEDIA_SOURCE_FORM,
    type: modal && modal.type,
    broadcastSources,
    initialValue: modal && modal.initialValue
  }));

  let { order: lastMediaOrder = DEFAULT_ORDER_NUMBER } = useSelector(
    selectLastMediaSource
  );

  const unAssignedMediaSources = getUnassignedMediaSources(sources);

  const dispatch = useDispatch();

  const clearSourceFormModal = () => dispatch(clearModal(type));

  /**
   * hide upload notification modal
   */
  const hideUploadStatusModal = useCallback(() => {
    dispatch(hideUploadStatus());
  }, [dispatch]);

  const { startFileUploadAction, endFileUploadAction } = useFileUploadAction();

  /**
   * Utilizes S3 Proxy Service to manage all the content uploads to the Studio S3 Buckets.
   * This functionality is the go-forward way to manage uploads done via {@link MEDIA_SOURCE_FORM}
   * @param source
   */
  const handleSubmitS3Proxy = async source => {
    hideUploadStatusModal();
    const fileUploadActionPayload = {
      title: source.title,
      fileType: source.value.type,
      extension: source.ext,
      isDragAndDrop: source.isDragAndDrop ?? false,
      isMultiFileUpload: source.isMultiFileUpload ?? false,
      fileSizeInBytes: source.value.size
    };
    const startFileUploadActionCallback = data => {
      startFileUploadAction({ ...data, ...fileUploadActionPayload });
    };
    const endFileUploadActionCallback = data => {
      endFileUploadAction({ ...data, ...fileUploadActionPayload });
    };
    if (source.sourceId) {
      dispatch(
        updateContentSource(
          source.sourceId,
          {
            type: getFileSourceType(source.value.name),
            title: source.title,
            ext: source.ext
          },
          source.value,
          { startFileUploadActionCallback, endFileUploadActionCallback }
        )
      );
    } else {
      dispatch(
        createContentSource(
          {
            ...source,
            type: getFileSourceType(source.value.name),
            order: lastMediaOrder + 1 // Increment last media source item order by 1
          },
          false,
          { startFileUploadActionCallback, endFileUploadActionCallback }
        )
      );
    }
    clearSourceFormModal();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={clearSourceFormModal}>
      <ModalHeader toggle={clearSourceFormModal}>Add content</ModalHeader>
      <ModalBody>
        <SourceForm
          hasFileInput
          onSubmit={handleSubmitS3Proxy}
          onCancel={clearSourceFormModal}
          sources={unAssignedMediaSources}
          source={initialValue}
          canAssignToPlaceholder
        />
      </ModalBody>
    </Modal>
  );
};

export default SourceFormModal;
